import { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useLazyQuery, gql } from "@apollo/client";
import {
    CmpTable,
    CountryTooltip,
    GetBasicLoginAppNameTooltip,
    Loader,
    SectionTitle,
} from "../../shared";
import SubComponent from "./SubComponent";
import { GET_ADMIN_USER_LIST, GET_EXCEL_ADMIN_USER_LIST } from "./query";
import { ADMIN_TYPES, Helper, exportToExcel } from "../../../utils";

const AdminUserList = ({ userType }) => {
    const title = Helper.toTitleCase(userType);
    const currentUserType = Number(useSelector((state) => state.users.currentUser.userType));
    const getUrl = currentUserType === 99 ? "/user-list" : "";

    let createUrl = "";
    if (currentUserType === 99) {
        if (
            userType === ADMIN_TYPES.SITEADMIN ||
            userType === ADMIN_TYPES.USERMANAGER ||
            userType === ADMIN_TYPES.SYSTEMADMIN
        ) {
            createUrl = `${getUrl}/${userType}/create`;
        }
    } else if (currentUserType === 3 && userType === ADMIN_TYPES.SITEADMIN) {
        createUrl = `${getUrl}/${userType}/create`;
    }

    const [getAdminExcelData, { loading }] = useLazyQuery(gql`
        ${GET_EXCEL_ADMIN_USER_LIST}
    `);

    const [filter, setFilter] = useState(undefined);

    const onFilterChange = (filter) => {
        setFilter(Object.keys(filter).length > 0 ? filter : undefined);
    };

    const excelExportAll = async (filter) => {
        try {
            const { data } = await getAdminExcelData({
                variables: {
                    getUserType,
                    filter: filter || undefined,
                },
            });

            if (data.getAdminUserList.nodes && data.getAdminUserList.nodes.length > 0) {
                const excelData = data.getAdminUserList.nodes.map((item) => {
                    const {
                        firstName,
                        lastName,
                        email,
                        country,
                        adminPermissions,
                        userByCreatorUserId,
                        status,
                        lastLogin,
                        createdAt,
                        loginCount,
                        phone,
                    } = item;

                    if (userType === "site-admin") {
                        return {
                            "First Name": firstName,
                            "Last Name": lastName,
                            Email: email,
                            Profile: Helper.toTitleCase(userType),
                            "System Access": Helper.formatAppList(adminPermissions),
                            "Country Access": Helper.formatCountryList(country),
                            "Created By": userByCreatorUserId
                                ? `${userByCreatorUserId.firstName} ${userByCreatorUserId.lastName}`
                                : "",
                            Status: Helper.formatStatus(status),
                            "Last Login": Helper.formatDate(lastLogin),
                            "Created Date": Helper.formatDate(createdAt),
                            "Login Count": loginCount || 0,
                            Phone: phone,
                        };
                    } else {
                        return {
                            "First Name": firstName,
                            "Last Name": lastName,
                            Email: email,
                            Profile: Helper.toTitleCase(userType),
                            "Created By": userByCreatorUserId
                                ? `${userByCreatorUserId.firstName} ${userByCreatorUserId.lastName}`
                                : "",
                            Status: Helper.formatStatus(status),
                            "Last Login": Helper.formatDate(lastLogin),
                            "Created Date": Helper.formatDate(createdAt),
                            "Login Count": loginCount || 0,
                            Phone: phone,
                        };
                    }
                });

                exportToExcel(
                    excelData,
                    `${Helper.toTitleCase(userType)} User Report${filter ? "(Filter)" : ""}`
                );
            } else {
                toast.error("No Data Available!!!");
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    const columns =
        userType === "site-admin"
            ? [
                  {
                      header: "Name",
                      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
                  },
                  {
                      accessorKey: "email",
                      header: "Email",
                  },
                  {
                      accessorKey: "country",
                      header: "Country",
                      cell: (cell) => (
                          <CountryTooltip
                              countryList={cell.getValue()}
                              id={`country-${cell.row.id}`}
                          />
                      ),
                  },
                  {
                      accessorKey: "adminPermissions",
                      header: "App Name",
                      cell: (cell) => (
                          <GetBasicLoginAppNameTooltip
                              appList={cell.getValue()}
                              id={`appName-${cell.row.id}`}
                          />
                      ),
                  },
                  {
                      accessorKey: "status",
                      header: "Status",
                      cell: (cell) => (cell.getValue() === 0 ? "Inactive" : "Active"),
                  },
              ]
            : [
                  {
                      header: "Name",
                      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
                  },
                  {
                      accessorKey: "email",
                      header: "Email",
                  },
                  {
                      accessorKey: "status",
                      header: "Status",
                      cell: (cell) => (cell.getValue() === 0 ? "Inactive" : "Active"),
                  },
              ];

    const getUserType =
        (userType === ADMIN_TYPES.SITEADMIN && 1) ||
        (userType === ADMIN_TYPES.USERMANAGER && 3) ||
        (userType === ADMIN_TYPES.SYSTEMADMIN && 99);

    const renderSubComponent = (data) => {
        return (
            <SubComponent
                data={data.row.original}
                adminType={userType}
                currentUserType={currentUserType}
            />
        );
    };
    return (
        <>
            {loading && <Loader />}
            <SectionTitle title={`${title} Users`} buttonUrl={createUrl} backUrl={getUrl || "/"}>
                <div className="dropdown mr-3">
                    <button
                        className="btn btn-outline-primary mb-0 dropdown-toggle"
                        type="button"
                        id="dropdownMenuButton"
                        data-toggle="dropdown"
                        aria-haspopup="true"
                        aria-expanded="false"
                    >
                        Download Data
                    </button>
                    <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
                        {filter && (
                            <button
                                type="button"
                                onClick={() => excelExportAll(filter)}
                                className="piap-btn btn btn-outline-primary mb-0 dropdown-item"
                            >
                                Download Filtered Data
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => excelExportAll()}
                            className="piap-btn btn btn-outline-primary mb-0 dropdown-item"
                        >
                            Download All Data
                        </button>
                    </div>
                </div>
            </SectionTitle>

            <CmpTable
                QUERY={GET_ADMIN_USER_LIST}
                headers={columns}
                renderSubComponent={renderSubComponent}
                dataPath="getAdminUserList"
                variables={{
                    getUserType,
                }}
                onFilterChange={onFilterChange}
                tableType="adminTable"
            />
        </>
    );
};

export default AdminUserList;
